import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import type { QueryKey, UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import { shouldRetry } from './retry';

export type HydraUIAPIQueryFunction<TResponse> = (context: { signal: AbortSignal }) => Promise<TResponse>;

export type HydraUIAPIQueryConfig<TResponse, TData = TResponse> = Omit<
  UseQueryOptions<TResponse, Error, TData>,
  'queryKey' | 'queryFn'
>;

export interface HydraUIAPIQueryOptionsInput<TQueryKey extends QueryKey, TResponse, TData = TResponse> {
  queryKey: TQueryKey;
  queryFn: HydraUIAPIQueryFunction<TResponse>;
  queryConfig?: HydraUIAPIQueryConfig<TResponse, TData>;
}

export function getHydraUIAPIQueryOptions<TQueryKey extends QueryKey, TResponse, TData = TResponse>({
  queryKey,
  queryFn,
  queryConfig,
}: HydraUIAPIQueryOptionsInput<TQueryKey, TResponse, TData>) {
  return queryOptions({
    queryKey,
    queryFn: ({ signal }) => queryFn({ signal }),
    retry: shouldRetry,
    ...queryConfig,
  });
}

export function useHydraUIAPIQuery<TQueryKey extends QueryKey, TResponse, TData = TResponse>(
  input: HydraUIAPIQueryOptionsInput<TQueryKey, TResponse, TData>,
) {
  return useQuery(getHydraUIAPIQueryOptions(input));
}

export type HydraUIAPIMutationConfig<TResponse, TVariables> = Omit<
  UseMutationOptions<TResponse, Error, TVariables>,
  'mutationFn'
>;

export function useHydraUIAPIMutation<TResponse, TVariables>(
  mutationFunction: (variables: TVariables) => Promise<TResponse>,
  mutationConfig?: HydraUIAPIMutationConfig<TResponse, TVariables>,
) {
  return useMutation({
    mutationFn: mutationFunction,
    ...mutationConfig,
  });
}
